import React, { useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus, Minus, ArrowRight } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const Cart = () => {
  const { items, removeItem, updateQty, total } = useCart();
  const { t } = useLang();
  const [method, setMethod] = useState("card");
  const [processing, setProcessing] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setProcessing(true);
    try {
      const origin_url = window.location.origin;
      const r = await api.post("/checkout/session", {
        items: items.map((i) => ({
          item_id: i.item_id,
          item_type: i.item_type,
          title: i.title,
          cover: i.cover,
          price: i.price,
          quantity: i.quantity,
        })),
        origin_url,
        payment_method: method,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Checkout failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout error");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div data-testid="cart-page" className="bg-[var(--monzal-bg)] min-h-screen">
      <div className="border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
          <div className="overline">CHECKOUT</div>
          <h1 className="font-display font-light text-4xl lg:text-6xl text-white tracking-tight mt-4">{t("cart.title")}</h1>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12">
        {items.length === 0 ? (
          <div className="text-center py-24" data-testid="cart-empty">
            <p className="text-gray-400 mb-8">{t("cart.empty")}</p>
            <Link to="/marketplace" className="btn-gold">
              {t("cart.exploreCta")} <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8 space-y-4">
              {items.map((it) => (
                <div key={it.item_id} data-testid={`cart-item-${it.item_id}`} className="card-luxury p-5 flex gap-5 items-center">
                  <div className="w-24 h-24 bg-black flex-shrink-0">
                    <img src={it.cover} alt={it.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="overline">{it.item_type}</div>
                    <Link to={it.item_type === "reptile" ? `/reptile/${it.item_id}` : `/product/${it.item_id}`} className="font-display text-white text-lg truncate block mt-1">{it.title}</Link>
                  </div>
                  <div className="flex items-center gap-3">
                    {it.item_type !== "reptile" && (
                      <div className="flex items-center border border-white/10">
                        <button data-testid={`cart-dec-${it.item_id}`} onClick={() => updateQty(it.item_id, it.quantity - 1)} className="p-2 text-gray-400 hover:text-white"><Minus size={12} /></button>
                        <span className="font-mono-tech text-sm text-white px-3">{it.quantity}</span>
                        <button data-testid={`cart-inc-${it.item_id}`} onClick={() => updateQty(it.item_id, it.quantity + 1)} className="p-2 text-gray-400 hover:text-white"><Plus size={12} /></button>
                      </div>
                    )}
                    <div className="text-right min-w-[100px]">
                      <div className="font-display text-[var(--monzal-gold)] text-base">${(it.price * it.quantity).toLocaleString()}</div>
                    </div>
                    <button data-testid={`cart-remove-${it.item_id}`} onClick={() => removeItem(it.item_id)} className="p-2 text-gray-500 hover:text-red-400"><X size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="lg:col-span-4">
              <div className="card-luxury p-6 sticky top-24">
                <div className="overline mb-5">{t("checkout.orderSummary")}</div>
                <div className="flex justify-between border-b border-white/5 pb-4">
                  <span className="text-gray-400">{t("cart.subtotal")}</span>
                  <span className="font-display text-[var(--monzal-gold)] text-lg" data-testid="cart-subtotal">
                    ${total.toLocaleString()}
                  </span>
                </div>
                <div className="mt-6">
                  <div className="overline mb-3">{t("cart.method")}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="cart-method-card" onClick={() => setMethod("card")} className={`py-3 text-xs uppercase tracking-[0.18em] font-display border transition ${method === "card" ? "border-[var(--monzal-gold)] text-[var(--monzal-gold)]" : "border-white/10 text-gray-400"}`}>
                      {t("cart.methodCard")}
                    </button>
                    <button data-testid="cart-method-oxxo" onClick={() => setMethod("oxxo")} className={`py-3 text-xs uppercase tracking-[0.18em] font-display border transition ${method === "oxxo" ? "border-[var(--monzal-gold)] text-[var(--monzal-gold)]" : "border-white/10 text-gray-400"}`}>
                      {t("cart.methodOxxo")}
                    </button>
                  </div>
                </div>
                <button
                  data-testid="checkout-btn"
                  data-testname="cart-checkout-btn"
                  onClick={handleCheckout}
                  disabled={processing}
                  className="btn-emerald-glow w-full justify-center mt-6 disabled:opacity-50"
                >
                  {processing ? "…" : t("cart.checkout")} <ArrowRight size={14} />
                </button>
                <p className="text-[10px] text-gray-500 mt-4 font-mono-tech leading-relaxed">
                  STRIPE · TEST MODE · 4242 4242 4242 4242
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
